
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {TradeHistoryRecord} from '../entity/TradeHistoryRecord';
import {FieldModelTradeHistoryRecord} from '../entity/TradeHistoryRecord';
import {OneOfEntityChange} from './OneOfEntityChange';
import {FieldModelOneOfEntityChange} from './OneOfEntityChange';

/**
 * SaveChangesParams struct
 */
class SaveChangesParams {
  /**
   * Initialize struct
   * @param {!Array=} newHistoryRecords
   * @param {!Array=} updates
   * @constructor
   */
  constructor (newHistoryRecords = [], updates = []) {
    this.NewHistoryRecords = newHistoryRecords
    this.Updates = updates
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SaveChangesParams}
   * @param {!SaveChangesParams} other Other struct
   * @returns {!SaveChangesParams} This struct
   */
  copy (other) {
    if (other.NewHistoryRecords != null) {
      this.NewHistoryRecords = []
      for (let item of other.NewHistoryRecords) {
        if (item != null) {
          let tempItem
          tempItem = TradeHistoryRecord.fromObject(item)
          this.NewHistoryRecords.push(tempItem)
        } else {
          this.NewHistoryRecords.push(null)
        }
      }
    } else {
      this.NewHistoryRecords = null
    }
    if (other.Updates != null) {
      this.Updates = []
      for (let item of other.Updates) {
        if (item != null) {
          let tempItem
          tempItem = OneOfEntityChange.fromObject(item)
          this.Updates.push(tempItem)
        } else {
          this.Updates.push(null)
        }
      }
    } else {
      this.Updates = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SaveChangesParams}
   * @returns {!SaveChangesParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SaveChangesParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SaveChangesParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SaveChangesParams}
   * @param {!SaveChangesParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SaveChangesParams)) {
      throw new TypeError('Instance of SaveChangesParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SaveChangesParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      NewHistoryRecords: ((this.NewHistoryRecords != null) ? Array.from(this.NewHistoryRecords, item => ((item != null) ? item : null)) : null),
      Updates: ((this.Updates != null) ? Array.from(this.Updates, item => ((item != null) ? item : null)) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SaveChangesParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SaveChangesParams} other Object value
   * @returns {!SaveChangesParams} Created struct
   */
  static fromObject (other) {
    return new SaveChangesParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SaveChangesParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SaveChangesParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SaveChangesParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4196
  }
}

export { SaveChangesParams };

/**
 * Fast Binary Encoding SaveChangesParams field model
 */
class FieldModelSaveChangesParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.NewHistoryRecords = new fbe.FieldModelVector(new FieldModelTradeHistoryRecord(buffer, 4 + 4), buffer, 4 + 4)
    this.Updates = new fbe.FieldModelVector(new FieldModelOneOfEntityChange(buffer, this.NewHistoryRecords.fbeOffset + this.NewHistoryRecords.fbeSize), buffer, this.NewHistoryRecords.fbeOffset + this.NewHistoryRecords.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSaveChangesParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSaveChangesParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.NewHistoryRecords.fbeSize + this.Updates.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSaveChangesParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.NewHistoryRecords.fbeExtra + this.Updates.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSaveChangesParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSaveChangesParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSaveChangesParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4196
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSaveChangesParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSaveChangesParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.NewHistoryRecords.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NewHistoryRecords.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewHistoryRecords.fbeSize

    if ((fbeCurrentSize + this.Updates.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Updates.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Updates.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSaveChangesParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSaveChangesParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSaveChangesParams}
   * @param {!SaveChangesParams} fbeValue Default value, defaults is new SaveChangesParams()
   * @returns {!SaveChangesParams} SaveChangesParams value
   */
  get (fbeValue = new SaveChangesParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSaveChangesParams}
   * @param {!SaveChangesParams} fbeValue SaveChangesParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.NewHistoryRecords.fbeSize) <= fbeStructSize) {
      this.NewHistoryRecords.get(fbeValue.NewHistoryRecords)
    } else {
      fbeValue.NewHistoryRecords.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NewHistoryRecords.fbeSize

    if ((fbeCurrentSize + this.Updates.fbeSize) <= fbeStructSize) {
      this.Updates.get(fbeValue.Updates)
    } else {
      fbeValue.Updates.length = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Updates.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSaveChangesParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSaveChangesParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSaveChangesParams}
   * @param {!SaveChangesParams} fbeValue SaveChangesParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSaveChangesParams}
   * @param {SaveChangesParams} fbeValue SaveChangesParams value
   */
  setFields (fbeValue) {
    this.NewHistoryRecords.set(fbeValue.NewHistoryRecords)
    this.Updates.set(fbeValue.Updates)
  }
}

export { FieldModelSaveChangesParams };

/**
 * Fast Binary Encoding SaveChangesParams model
 */
class SaveChangesParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSaveChangesParams(this.buffer, 4)
  }

  /**
   * Get the SaveChangesParams model
   * @this {!SaveChangesParamsModel}
   * @returns {!FieldModelSaveChangesParams} model SaveChangesParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SaveChangesParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SaveChangesParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SaveChangesParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SaveChangesParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSaveChangesParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SaveChangesParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SaveChangesParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SaveChangesParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SaveChangesParamsModel}
   * @param {!SaveChangesParams} value SaveChangesParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SaveChangesParamsModel}
   * @param {!SaveChangesParams} value SaveChangesParams value, defaults is new SaveChangesParams()
   * @return {!object} Deserialized SaveChangesParams value and its size
   */
  deserialize (value = new SaveChangesParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SaveChangesParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SaveChangesParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SaveChangesParamsModel}
   * @param {!number} prev Previous SaveChangesParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SaveChangesParamsModel };
